/**
 * File-source helpers: category palette, file-type → category map, VFS scheme
 * labels, and human-readable byte sizes. Used by FileTypeChip, the document
 * corpus overview, and the data sources table.
 */

export const FILE_TYPE_COLORS = {
  columnar: '#16A34A',
  document: '#4F46E5',
  json:     '#F59E0B',
  image:    '#8A92A0',
  archive:  '#5C6473',
};

// Style-guide fixed per-format palette (chip backgrounds, white text). Keyed by
// both raw FileNodeType and DatasourceType values. Formats not listed here fall
// back to their category color from FILE_TYPE_COLORS via FILE_NODE_CATEGORY
// (documents → indigo, images → neutral, archives → slate).
export const FORMAT_COLORS = {
  JSON: '#F59E0B', FILE_JSON_FOLDER: '#F59E0B',
  CSV: '#16A34A', FILE_DELIMITED_FOLDER: '#16A34A',
  TSV: '#0891B2',
  PARQUET: '#8B5CF6', FILE_PARQUET_FOLDER: '#8B5CF6',
  XML: '#5C6473', FILE_XML_FOLDER: '#5C6473',
  EXCEL: '#0F766E', FILE_EXCEL_FOLDER: '#0F766E',
  FIXED_WIDTH: '#64748B', FILE_FIXED_WIDTH_FOLDER: '#64748B',
  HL7: '#DC2626', FILE_HL7_FOLDER: '#DC2626',
  EDI: '#EA580C', FILE_EDI_FOLDER: '#EA580C',
  TEXT: '#8A92A0',
  UNKNOWN: '#B8BFC9',
};

// Same hues shifted dark enough to pass as 12px text on a white surface —
// FORMAT_COLORS are chip backgrounds and several (amber, green, violet, cyan)
// are too light as foreground text. Only the problem hues are overridden.
const FORMAT_TEXT_OVERRIDES = {
  JSON: '#B45309', FILE_JSON_FOLDER: '#B45309',
  CSV: '#15803D', FILE_DELIMITED_FOLDER: '#15803D',
  TSV: '#0E7490',
  PARQUET: '#7C3AED', FILE_PARQUET_FOLDER: '#7C3AED',
  UNKNOWN: '#8A92A0',
};

/**
 * Text color for rendering a file name keyed to its sniffed format — same hue
 * family as the format's chip so names and pills read as one system. Returns
 * null for unknown/absent types so callers can keep their default link color.
 */
export function formatTextColor(type) {
  if (!type) return null;
  return (
    FORMAT_TEXT_OVERRIDES[type] ||
    FORMAT_COLORS[type] ||
    FILE_TYPE_COLORS[FILE_NODE_CATEGORY[type]] ||
    null
  );
}

// Maps both raw FileNodeType values (CSV, TSV, PARQUET, PDF, …) and saved
// DatasourceType values (FILE_DELIMITED_FOLDER, FILE_PARQUET_FOLDER, …) onto
// the colour categories above. Used by FileTypeChip.
export const FILE_NODE_CATEGORY = {
  CSV: 'columnar', TSV: 'columnar', PARQUET: 'columnar', EXCEL: 'columnar',
  FIXED_WIDTH: 'columnar', HL7: 'columnar', EDI: 'columnar',
  FILE_DELIMITED_FOLDER: 'columnar', FILE_PARQUET_FOLDER: 'columnar',
  FILE_EXCEL_FOLDER: 'columnar', FILE_FIXED_WIDTH_FOLDER: 'columnar',
  FILE_HL7_FOLDER: 'columnar', FILE_EDI_FOLDER: 'columnar',
  PDF: 'document', DOCX: 'document', TXT: 'document', TEXT: 'document',
  RTF: 'document', EML: 'document', PPTX: 'document', HTML: 'document',
  FILE_DOCUMENT_FOLDER: 'document',
  JSON: 'json', XML: 'json',
  FILE_JSON_FOLDER: 'json', FILE_XML_FOLDER: 'json',
  PNG: 'image', JPG: 'image', JPEG: 'image', BMP: 'image', GIF: 'image',
  ZIP: 'archive', TAR: 'archive', GZ: 'archive',
};

/**
 * Mirror of c2-discover-api's `DatasourceType.isFileBased()` — returns true for the
 * format-specific FILE_*_FOLDER values, false for everything else (RELATIONAL, NOSQL,
 * the legacy FILE_FOLDER orphan, undefined). Keep this set in sync with the worker
 * enum; adding a value on the backend without updating this list silently mislabels
 * results-view headers.
 */
const FILE_BASED_DATASOURCE_TYPES = new Set([
  'FILE_DELIMITED_FOLDER',
  'FILE_PARQUET_FOLDER',
  'FILE_DOCUMENT_FOLDER',
  'FILE_JSON_FOLDER',
  'FILE_XML_FOLDER',
  'FILE_EXCEL_FOLDER',
  'FILE_FIXED_WIDTH_FOLDER',
  'FILE_HL7_FOLDER',
  'FILE_EDI_FOLDER',
  'FILE_GENERIC_FOLDER',
  'FILE_SQL_DUMP_FOLDER',
]);

export function isFileBasedDatasourceType(datasourceType) {
  return FILE_BASED_DATASOURCE_TYPES.has(datasourceType);
}

/**
 * Lenient file predicate for *labeling* only: the strict backend-mirror set above PLUS
 * the legacy `FILE_FOLDER` orphan. A `FILE_FOLDER` source (old sftp/s3 rows) is still a
 * folder of files to the user, so it should read "files"/"folder" even though the strict
 * mirror excludes it. Do NOT use this to gate behavior (structured-tree views, masking
 * routing) — those must stay on `isFileBasedDatasourceType`, which mirrors the worker enum.
 */
export function isFileLikeDatasourceType(datasourceType) {
  return FILE_BASED_DATASOURCE_TYPES.has(datasourceType) || datasourceType === 'FILE_FOLDER';
}

/**
 * Formats whose findings are tree-shaped leaf paths rather than flat columns —
 * a parsed JSON/XML file has no "columns", so results views should render its
 * whole field set through the structured (tree) view instead of the relational
 * columns view. Accepts the DatasourceType vocabulary used by both a source's
 * `datasourceType` and a results row's sniffed `sampler_type`. NOSQL is here
 * because a Mongo collection's fields are the same dotted document paths a
 * JSON file produces (contact.email, orders[].sku) — the collection drill-in
 * presents as a tree, exactly like a JSON file table.
 */
const STRUCTURED_TREE_TYPES = new Set(['FILE_JSON_FOLDER', 'FILE_XML_FOLDER', 'NOSQL']);

export function isStructuredTreeType(type) {
  return STRUCTURED_TREE_TYPES.has(type);
}

/**
 * Display labels for VFS scheme codes. Used wherever a stored data source's
 * `vfsScheme` is rendered to the user (data sources table, source detail, etc.).
 * Falls back to the raw code for unknown values so we don't silently hide a
 * mismatch between UI and worker enums.
 */
export const VFS_SCHEME_LABELS = {
  LOCAL: 'Local filesystem',
  SFTP: 'SFTP',
  S3: 'Amazon S3',
  AZURE_BLOB: 'Azure Blob',
  SHAREPOINT: 'SharePoint',
};

export function vfsSchemeLabel(scheme) {
  if (!scheme) return '';
  return VFS_SCHEME_LABELS[scheme] || scheme;
}

/**
 * Display type for a content-sniffed verdict. The worker's FormatSniffer labels any
 * readable text it can't classify more specifically as DOCX (its Tika document
 * bucket), and that label is persisted as `sniffedType`. When the file's declared
 * (extension-derived) type is UNKNOWN — .java, .sh, .log, extensionless — rendering
 * that fallback as a DOCX chip reads as "identified as a Word document" when all we
 * actually know is "it's text". Show a generic TEXT type instead. Only the display
 * changes; mismatch detection and masking routing keep the raw sniffedType.
 */
export function displaySniffType(declaredType, sniffedType) {
  if (sniffedType === 'DOCX' && (!declaredType || declaredType === 'UNKNOWN')) return 'TEXT';
  return sniffedType;
}

/**
 * Shorten a fully-qualified field name for display. JSON and XML field paths can
 * grow to dozens of dotted segments (e.g. `orders[].customer.email`); rendering
 * the full path eats horizontal space in the field list. Shows the last two
 * segments — enough disambiguation in practice — and the call site is expected
 * to add `title={fullName}` so the complete path is reachable on hover. CSV and
 * relational column names contain no dots, so this is a no-op for them.
 */
export function displayFieldName(fieldName) {
  if (!fieldName || !fieldName.includes('.')) return fieldName || '—';
  const segments = fieldName.split('.');
  return segments.slice(-2).join('.');
}

export function formatBytes(bytes) {
  if (bytes == null) return '—';
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '—';
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/**
 * Parent folder of a scan-root-relative row name. MUST mirror the worker's
 * `FileGenericRunService.parentFolderOf` — the chunk endpoints group rows by this
 * derivation, so a divergent copy here would look up the wrong chunk.
 * "" = directly under the scan root; archive boundaries survive
 * ("sub/bundle.zip!/inner.csv" -> "sub/bundle.zip!").
 *
 * Lives here rather than in DataDiscovery/discoverApi so that pure consumers (the Enterprise
 * landscape's area derivation) can use it without pulling in the API client — and so that
 * suites which jest.mock the api client don't lose it. discoverApi re-exports this binding.
 */
export function parentFolderOfPath(relative) {
  if (relative == null) return '';
  const slash = Math.max(relative.lastIndexOf('/'), relative.lastIndexOf('\\'));
  return slash <= 0 ? '' : relative.slice(0, slash);
}
